import React, { useState, useCallback, useEffect } from 'react';
import { CommunityLocation, CommunityComprehensiveAnalytics, ComparisonMode } from '../types/api';
import { getCareColor, colorPalette } from '../utils/careColors';
import { geoAPI } from '../services/api';
import {
  Groups,
  CalendarMonth,
  Star
} from '@mui/icons-material';
import { Alert, AlertTitle } from '@mui/material';

interface CommunityCardProps {
  community: CommunityLocation;
  isFiltered: boolean;
  isSelected: boolean;
  onCommunityClick: (community: CommunityLocation) => void;
  onCommunityHover: (communityId: string | null) => void;
  currentCareLevel?: string;
  comparisonMode?: ComparisonMode;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  community,
  isFiltered,
  isSelected,
  onCommunityClick,
  onCommunityHover,
  currentCareLevel,
  comparisonMode = 'radius'
}) => {
  const [comprehensiveAnalytics, setComprehensiveAnalytics] = useState<CommunityComprehensiveAnalytics | null>(null);
  const [loadingComprehensive, setLoadingComprehensive] = useState(false);
  const [comprehensiveError, setComprehensiveError] = useState<string | null>(null);

  const isCorpComparison = comparisonMode === 'corporation';
  // Benchmark wording depends on the comparison mode chosen in the sidebar.
  const benchmarkLabel = isCorpComparison ? 'Corporation Average' : 'Competitor';
  const benchmarkShortLabel = isCorpComparison ? 'corp avg' : 'competitors';

  // Helper function for bar colors
  const getBarColor = (category: string, isCompetitor: boolean = false) => {
    const colors = {
      'Very Poor': isCompetitor ? '#ffcdd2' : colorPalette.danger,
      'Poor': isCompetitor ? '#ffe0b2' : '#ff9800',
      'Neither Poor Nor Good': isCompetitor ? '#f3e5f5' : colorPalette.secondary,
      'Good': isCompetitor ? '#c8e6c9' : '#4caf50',
      'Very Good': isCompetitor ? '#dcedc8' : colorPalette.success
    };
    return colors[category as keyof typeof colors] || colorPalette.secondary;
  };

  // Fetch comprehensive analytics data
  const fetchComprehensiveAnalytics = useCallback(async () => {
    if (loadingComprehensive || comprehensiveAnalytics) {
      return;
    }

    try {
      setLoadingComprehensive(true);
      setComprehensiveError(null);
      const analytics = await geoAPI.getCommunityComprehensiveAnalytics(
        community.community_id,
        80,
        currentCareLevel || undefined,
        comparisonMode
      );
      setComprehensiveAnalytics(analytics);
    } catch (error) {
      console.error('Error fetching comprehensive analytics:', error);
      setComprehensiveError(error instanceof Error ? error.message : 'Failed to load comprehensive analytics');
    } finally {
      setLoadingComprehensive(false);
    }
  }, [community.community_id, loadingComprehensive, comprehensiveAnalytics, currentCareLevel, comparisonMode]);

  // Refetch the benchmark when the comparison mode or care level changes:
  // clearing cached analytics lets the load effect below pull fresh data.
  useEffect(() => {
    setComprehensiveAnalytics(null);
    setComprehensiveError(null);
  }, [comparisonMode, currentCareLevel]);

  // QoL and QoH use different rating scales; keys must match the API's histogram categories
  const HISTOGRAM_SCALES = {
    QoL: {
      keys: ['VERY_POOR', 'POOR', 'NEITHER_POOR_NOR_GOOD', 'GOOD', 'VERY_GOOD'],
      short: ['VP', 'P', 'M', 'G', 'VG'],
      full: ['Very Poor', 'Poor', 'Neither Poor Nor Good', 'Good', 'Very Good']
    },
    QoH: {
      keys: ['POOR', 'FAIR', 'GOOD', 'VERY_GOOD', 'EXCELLENT'],
      short: ['P', 'F', 'G', 'VG', 'E'],
      full: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']
    }
  } as const;

  // Convert comprehensive analytics histogram to old format for compatibility
  const convertHistogramToOldFormat = (histogramData: any, metricType: 'QoH' | 'QoL') => {
    if (!histogramData) return [0, 0, 0, 0, 0];
    return HISTOGRAM_SCALES[metricType].keys.map(key => histogramData[key]?.percentage || 0);
  };

  const renderMiniTable = (score: number, metricType: 'QoH' | 'QoL', communityName: string) => {
    // Get histogram data from comprehensive analytics
    const histogramData = metricType === 'QoH'
      ? comprehensiveAnalytics?.community_metrics?.qoh_histogram
      : comprehensiveAnalytics?.community_metrics?.qol_histogram;

    // Get competitor histogram data
    const competitorHistogramData = metricType === 'QoH'
      ? comprehensiveAnalytics?.competitor_metrics?.qoh_histogram
      : comprehensiveAnalytics?.competitor_metrics?.qol_histogram;

    const isLoading = loadingComprehensive;
    const hasError = !!comprehensiveError;
    const hasData = !!histogramData;
    const hasCompetitorData = !!competitorHistogramData;

    // Define the data structure type
    interface TableData {
      community: number[];
      competitor: number[];
    }

    // Use histogram data from comprehensive analytics
    let data: TableData;
    let dataSource: 'real' | 'empty' | 'loading' | 'error';

    if (hasData && histogramData) {
      // Convert comprehensive analytics histogram to the expected format
      data = {
        community: convertHistogramToOldFormat(histogramData, metricType),
        competitor: hasCompetitorData ? convertHistogramToOldFormat(competitorHistogramData, metricType) : [0, 0, 0, 0, 0]
      };
      dataSource = 'real';
    } else if (hasError) {
      // Show zeros for communities with errors
      data = {
        community: [0, 0, 0, 0, 0],
        competitor: [0, 0, 0, 0, 0]
      };
      dataSource = 'error';
    } else if (isLoading) {
      // Show zeros while loading
      data = {
        community: [0, 0, 0, 0, 0],
        competitor: [0, 0, 0, 0, 0]
      };
      dataSource = 'loading';
    } else {
      // Show zeros for empty state
      data = {
        community: [0, 0, 0, 0, 0],
        competitor: [0, 0, 0, 0, 0]
      };
      dataSource = 'empty';
    }

    const categories = HISTOGRAM_SCALES[metricType].short;
    const fullCategories = HISTOGRAM_SCALES[metricType].full;

    return (
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: '3px',
        overflow: 'hidden',
        fontSize: '7px',
        marginTop: '4px',
        backgroundColor: 'white',
        opacity: isLoading ? 0.7 : 1,
        position: 'relative'
      }}
      title={
        dataSource === 'real' ?
          `Real histogram data${hasCompetitorData ? ` with ${benchmarkShortLabel} data` : ' (community only)'}` :
        dataSource === 'loading' ? 'Loading histogram data...' :
        dataSource === 'error' ? 'Error loading histogram data' :
        'No histogram data available'
      }>
        {/* Loading indicator */}
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '8px',
            color: '#666',
            zIndex: 1,
            backgroundColor: 'rgba(255,255,255,0.9)',
            padding: '2px 4px',
            borderRadius: '2px'
          }}>
            Loading...
          </div>
        )}

        {/* No data/Error indicator */}
        {(dataSource === 'empty' || dataSource === 'error') && !isLoading && (
          <div style={{
            position: 'absolute',
            top: '2px',
            left: '2px',
            fontSize: '6px',
            color: dataSource === 'error' ? colorPalette.danger : '#999',
            zIndex: 1,
            backgroundColor: 'rgba(255,255,255,0.8)',
            padding: '1px 2px',
            borderRadius: '1px'
          }}>
            {dataSource === 'error' ? 'Error' : 'No data'}
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '25px repeat(5, 1fr)',
          backgroundColor: '#f5f5f5',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          <div style={{ padding: '2px', borderRight: '1px solid #e0e0e0', textAlign: 'center' }}></div>
          {categories.map((cat, index) => (
            <div title={fullCategories[index]} key={index} style={{
              padding: '2px',
              borderRight: index < categories.length - 1 ? '1px solid #e0e0e0' : 'none',
              textAlign: 'center'
            }}>
              {cat}
            </div>
          ))}
        </div>

        {/* Community Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '25px repeat(5, 1fr)',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <div style={{
            padding: '2px',
            borderRight: '1px solid #e0e0e0',
            fontSize: '9px',
            fontWeight: 'bold',
            backgroundColor: '#fafafa',
            textAlign: 'center'
          }}>
            You
          </div>
          {data.community.map((percentage, index) => (
            <div title='Your Community' key={index} style={{
              padding: '2px',
              borderRight: index < data.community.length - 1 ? '1px solid #e0e0e0' : 'none',
              textAlign: 'center',
              fontSize: '12px',
              backgroundColor: getBarColor(fullCategories[index], false),
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {percentage}%
            </div>
          ))}
        </div>

        {/* Competitor Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '25px repeat(5, 1fr)'
        }}>
          <div title={benchmarkLabel} style={{
            padding: '2px',
            borderRight: '1px solid #e0e0e0',
            fontSize: '9px',
            fontWeight: 'bold',
            backgroundColor: '#fafafa',
            textAlign: 'center'
          }}>
            VS
          </div>
          {data.competitor.map((percentage, index) => (
            <div key={index} style={{
              padding: '2px',
              borderRight: index < data.competitor.length - 1 ? '1px solid #e0e0e0' : 'none',
              textAlign: 'center',
              fontSize: '12px',
              backgroundColor: getBarColor(fullCategories[index], true),
              color: '#666',
              fontWeight: 'bold'
            }}>
              {percentage}%
            </div>
          ))}
        </div>

        {/* Data source indicator */}
        {!isLoading && (
          <div style={{
            position: 'absolute',
            bottom: '1px',
            right: '2px',
            fontSize: '6px',
            color: dataSource === 'real' ? '#4caf50' : '#999',
            fontWeight: 'bold'
          }}>
            {dataSource === 'real' ? '●' : '○'}
          </div>
        )}
      </div>
    );
  };

    useEffect(() => {
        let isMounted = true;

        const loadAllData = async () => {

        try {
            await Promise.allSettled([fetchComprehensiveAnalytics()]);
        } catch (error) {
        if (isMounted) {
            console.error('Error loading data:', error);
            }
        }
        };

        loadAllData();

        // Cleanup to avoid setting state after unmount
        return () => {
            isMounted = false;
        };
    }, [community.community_id, fetchComprehensiveAnalytics]);

  return (
    <div
      onClick={() => onCommunityClick(community)}
      style={{
        padding: '12px',
        margin: '4px 0',
        backgroundColor: isSelected ? '#e3f2fd' :
          isFiltered ? '#f0f8ff' : '#f9f9f9',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: isSelected ? `2px solid ${colorPalette.primary}` :
          isFiltered ? '2px solid #87ceeb' : '1px solid #eee',
        opacity: loadingComprehensive ? 0.8 : 1,
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        onCommunityHover(community.community_id);
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = isFiltered ? '#e6f3ff' : '#f0f0f0';
        }
      }}
      onMouseLeave={(e) => {
        onCommunityHover(null);
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = isFiltered ? '#f0f8ff' : '#f9f9f9';
        }
      }}
    >
      <div style={{ fontWeight: 'bold', fontSize: '22px', marginBottom: '4px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span>{community.community_name}</span>
        {community.care_level && (
          <span style={{
            backgroundColor: getCareColor(community.care_level),
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '11px',
            fontWeight: 'bold'
          }}>
            {
                community.care_level.map(level => typeof level === 'string' ? level.toUpperCase() : level).join(', ')
            }
          </span>
        )}
        {community.resident_count && (
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f0f0f0',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>
            <Groups fontSize="inherit" /> {community.resident_count}
          </span>
        )}
      </div>

      {community.corporation_name && (
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '2px' }}>
          {community.corporation_name}
        </div>
      )}

      {(comprehensiveAnalytics?.community_metrics?.los?.mean || community.avg_days_in_residence || community.days_in_residence) && (
        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CalendarMonth fontSize="inherit" /> Avg LOS: {Math.round(
                comprehensiveAnalytics?.community_metrics?.los?.mean ||
                community.avg_days_in_residence ||
                community.days_in_residence ||
                0
              )} days
            </div>
            {comprehensiveAnalytics?.competitor_metrics?.los?.mean && (
              <div style={{
                fontSize: '10px',
                fontWeight: 'bold',
                marginTop: '2px',
                color: ((comprehensiveAnalytics.community_metrics?.los?.mean || community.avg_days_in_residence || community.days_in_residence || 0) - comprehensiveAnalytics.competitor_metrics.los.mean) >= 0 ? colorPalette.success : colorPalette.danger
              }}>
                {((comprehensiveAnalytics.community_metrics?.los?.mean || community.avg_days_in_residence || community.days_in_residence || 0) - comprehensiveAnalytics.competitor_metrics.los.mean) >= 0 ? '+' : ''}
                {Math.round((comprehensiveAnalytics.community_metrics?.los?.mean || community.avg_days_in_residence || community.days_in_residence || 0) - comprehensiveAnalytics.competitor_metrics.los.mean)} days vs {benchmarkShortLabel}
              </div>
            )}
          </div>

          {comprehensiveAnalytics?.competitor_metrics?.los?.mean && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>
                vs {Math.round(comprehensiveAnalytics.competitor_metrics.los.mean)} days
              </div>
            </div>
          )}
        </div>
      )}      {/* Data Loading Errors Summary */}
      {comprehensiveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Failed to Load Data</AlertTitle>
          <div>• Comprehensive analytics: {comprehensiveError}</div>
        </Alert>
      )}

      {/* Quality Metrics with Competitor Comparisons */}
      {(comprehensiveAnalytics?.community_metrics?.qoh || comprehensiveAnalytics?.community_metrics?.qol || community.avg_qoh_score || community.avg_qol_score) && (
        <div style={{ marginTop: '6px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <div style={{ display: 'flex', gap: '8px'  }}>
            {/* Left Column - QoH */}
            {(comprehensiveAnalytics?.community_metrics?.qoh || community.avg_qoh_score) && (
              <div style={{ flex: 1 }}>
                {/* QoH Metrics */}
                <div style={{ fontSize: '16px', color: colorPalette.info, display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}>
                    <Star fontSize="inherit" style={{ color: colorPalette.info }} /> QoH: {
                      comprehensiveAnalytics?.community_metrics?.qoh?.mean?.toFixed(1) ||
                      community.avg_qoh_score?.toFixed(1) ||
                      'N/A'
                    }
                  </span>
                  {(comprehensiveAnalytics?.competitor_metrics?.qoh || community.competitor_avg_qoh) && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        vs {(comprehensiveAnalytics?.competitor_metrics?.qoh?.mean || community.competitor_avg_qoh)?.toFixed(1)}
                      </div>
                      {community.qoh_percentile !== undefined && community.qoh_percentile !== null && (
                        <div style={{ fontSize: '9px', color: colorPalette.secondary }}>
                          {community.qoh_percentile.toFixed(0)}th percentile
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '10px', marginTop: '0px', marginBottom: '8px' }}>
                  {((comprehensiveAnalytics?.community_metrics?.qoh && comprehensiveAnalytics?.competitor_metrics?.qoh) || community.qoh_advantage !== undefined) && (
                    <div style={{
                      color: (comprehensiveAnalytics?.community_metrics?.qoh && comprehensiveAnalytics?.competitor_metrics?.qoh ?
                        (comprehensiveAnalytics.community_metrics.qoh.mean - comprehensiveAnalytics.competitor_metrics.qoh.mean) :
                        community.qoh_advantage || 0) >= 0 ? colorPalette.success : colorPalette.danger,
                      fontWeight: 'bold'
                    }}>
                      {comprehensiveAnalytics?.community_metrics?.qoh && comprehensiveAnalytics?.competitor_metrics?.qoh ?
                        (comprehensiveAnalytics.community_metrics.qoh.mean - comprehensiveAnalytics.competitor_metrics.qoh.mean >= 0 ? '+' : '') +
                        (comprehensiveAnalytics.community_metrics.qoh.mean - comprehensiveAnalytics.competitor_metrics.qoh.mean).toFixed(1) :
                        (community.qoh_advantage && community.qoh_advantage >= 0 ? '+' : '') + (community.qoh_advantage?.toFixed(1) || '0')
                      } vs {benchmarkShortLabel}
                    </div>
                  )}
                </div>
                {/* QoH Mini Table */}
                {renderMiniTable(
                  comprehensiveAnalytics?.community_metrics?.qoh?.mean || community.avg_qoh_score || 0,
                  'QoH',
                  community.community_name
                )}
              </div>
            )}

            {/* Vertical Separator */}
            {(comprehensiveAnalytics?.community_metrics?.qoh || community.avg_qoh_score) &&
             (comprehensiveAnalytics?.community_metrics?.qol || community.avg_qol_score) && (
              <div style={{
                width: '1px',
                backgroundColor: '#ddd',
                margin: '0 4px',
                alignSelf: 'stretch'
              }} />
            )}

            {/* Right Column - QoL */}
            {(comprehensiveAnalytics?.community_metrics?.qol || community.avg_qol_score) && (
              <div style={{ flex: 1 }}>
                {/* QoL Metrics */}
                <div style={{ fontSize: '16px', color: colorPalette.warning, display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}>
                    <Star fontSize="inherit" style={{ color: colorPalette.warning }} /> QoL: {
                      comprehensiveAnalytics?.community_metrics?.qol?.mean?.toFixed(1) ||
                      community.avg_qol_score?.toFixed(1) ||
                      'N/A'
                    }
                  </span>
                  {(comprehensiveAnalytics?.competitor_metrics?.qol || community.competitor_avg_qol) && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        vs {(comprehensiveAnalytics?.competitor_metrics?.qol?.mean || community.competitor_avg_qol)?.toFixed(1)}
                      </div>
                      {community.qol_percentile !== undefined && community.qol_percentile !== null && (
                        <div style={{ fontSize: '9px', color: colorPalette.secondary }}>
                          {community.qol_percentile.toFixed(0)}th percentile
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '10px', marginTop: '0px', marginBottom: '8px' }}>
                  {((comprehensiveAnalytics?.community_metrics?.qol && comprehensiveAnalytics?.competitor_metrics?.qol) || community.qol_advantage !== undefined) && (
                    <div style={{
                      color: (comprehensiveAnalytics?.community_metrics?.qol && comprehensiveAnalytics?.competitor_metrics?.qol ?
                        (comprehensiveAnalytics.community_metrics.qol.mean - comprehensiveAnalytics.competitor_metrics.qol.mean) :
                        community.qol_advantage || 0) >= 0 ? colorPalette.success : colorPalette.danger,
                      fontWeight: 'bold'
                    }}>
                      {comprehensiveAnalytics?.community_metrics?.qol && comprehensiveAnalytics?.competitor_metrics?.qol ?
                        (comprehensiveAnalytics.community_metrics.qol.mean - comprehensiveAnalytics.competitor_metrics.qol.mean >= 0 ? '+' : '') +
                        (comprehensiveAnalytics.community_metrics.qol.mean - comprehensiveAnalytics.competitor_metrics.qol.mean).toFixed(1) :
                        (community.qol_advantage && community.qol_advantage >= 0 ? '+' : '') + (community.qol_advantage?.toFixed(1) || '0')
                      } vs {benchmarkShortLabel}
                    </div>
                  )}
                </div>
                {/* QoL Mini Table */}
                {renderMiniTable(
                  comprehensiveAnalytics?.community_metrics?.qol?.mean || community.avg_qol_score || 0,
                  'QoL',
                  community.community_name
                )}
              </div>
            )}
          </div>

          {(comprehensiveAnalytics?.competitor_metrics?.competitor_count || community.competitor_count) && (
            <div style={{ fontWeight: 600, fontSize: '12px', color: '#999', marginTop: '4px', textAlign: 'center', borderTop: '1px solid #eee', paddingTop: '4px' }}>
              {(() => {
                const count = comprehensiveAnalytics?.competitor_metrics?.competitor_count || community.competitor_count;
                return isCorpComparison
                  ? `Based on ${count} other ${count === 1 ? 'community' : 'communities'} in this corporation`
                  : `Based on ${count} competitors within 80 miles`;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Loading indicator for the whole card */}
      {loadingComprehensive && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: colorPalette.warning,
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}

      {/* Error indicator for comprehensive analytics */}
      {comprehensiveError && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: loadingComprehensive ? '20px' : '8px',
          fontSize: '10px',
          color: colorPalette.danger,
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '2px 4px',
          borderRadius: '2px',
          border: `1px solid ${colorPalette.danger}`
        }} title={comprehensiveError}>
          Analytics Error
        </div>
      )}

      {/* Add CSS animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default CommunityCard;
